import type {PropsWithChildren} from 'react'
import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {WithResourcesInternal} from '@freckle/educator-entities/ts/common/components/with-resources'
import {
  isLocalhost,
  isStaging,
  isTestEnv
} from '@freckle/educator-entities/ts/common/helpers/common-api-helper'
import {equalExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useDistricts} from '@freckle/educator-entities/ts/common/hooks/use-districts'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {DistrictAttrs} from '@freckle/educator-entities/ts/roster/models/district'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {
  CHROMELESS_PENDO_APP_KEY,
  DEFAULT_PENDO_APP_KEY,
  ensurePendoStub,
  loadPendoAgent
} from '@freckle/classroom/ts/common/helpers/pendo-loader'
import {logError} from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'
import {WithAuthStatus} from '@freckle/classroom/ts/common/routers/router-v2/with-auth-status'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useChromeless} from '@freckle/classroom/ts/hooks/chromeless/hook'
import {
  useHasStarPlacements,
  placementsMayExist
} from '@freckle/classroom/ts/hooks/placement-summaries'
import {usePremiumLicenses} from '@freckle/classroom/ts/hooks/use-premium-licenses'
import {useStudents} from '@freckle/classroom/ts/hooks/use-students'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {initializePendo} from './../app-router'
import {RedirectSet} from './cookie-redirect'

type Props = {
  teacher: TeacherAttrs
  premiumLicenses: PremiumLicensesAttrs
  students: Array<StudentAttrs>
  schools: SchoolAttrs[]
  districts: DistrictAttrs[]
}

// Initializes Pendo and renders nothing. If the agent fails to load, the error is logged and the
// app keeps working without Pendo.
export function PendoInner(props: Props) {
  const {teacher, premiumLicenses, students, schools, districts} = props
  const hasInitialized = useRef(false)
  const chromeless = useChromeless()
  const hasStarMathPlacements = useHasStarPlacements('math', {
    enabled: placementsMayExist('math', schools, premiumLicenses)
  })
  const hasStarReadingPlacements = useHasStarPlacements('ela', {
    enabled: placementsMayExist('ela', schools, premiumLicenses)
  })

  useSafeEffectExtraDeps<Props>(
    ({teacher, schools, districts, premiumLicenses, students}) => {
      const shouldInitialize = !(isStaging() || isTestEnv() || isLocalhost())
      const appKey = chromeless ? CHROMELESS_PENDO_APP_KEY : DEFAULT_PENDO_APP_KEY
      const loadAgent = () => (shouldInitialize ? loadPendoAgent(appKey) : Promise.resolve())
      const initialize = () =>
        initializePendo({
          teacher,
          districts,
          schools,
          premiumLicenses,
          students,
          hasStarMathPlacements,
          hasStarReadingPlacements
        })
      const onAgentError = (error: unknown) =>
        logError(error instanceof Error ? error : new Error(String(error)))

      if (!hasInitialized.current) {
        // Initialize before the agent loads: the stub queues `initialize` ahead of any calls the
        // page has already made, and the agent replays the queue when it loads
        hasInitialized.current = true
        ensurePendoStub()
        initialize()
        loadAgent().catch(onAgentError)
      } else {
        // Updated data, e.g. placements loaded. The stub would queue this `initialize` ahead of the
        // first one, so wait for the agent instead.
        loadAgent().then(initialize, onAgentError)
      }
    },
    [hasStarMathPlacements, hasStarReadingPlacements, chromeless],
    {
      teacher: equalExtra(teacher),
      schools: equalExtra(schools),
      districts: equalExtra(districts),
      premiumLicenses: equalExtra(premiumLicenses),
      students: equalExtra(students)
    }
  )

  return null
}

// Renders the page right away and initializes Pendo alongside it, instead of waiting for Pendo's
// data and agent. Pendo calls made in the meantime are queued by the `window.pendo` stub.
export const PendoWrapper = (props: PropsWithChildren<object>) => {
  const location = useLocation()
  return (
    <WithAuthStatus
      render={({loggedIn}) =>
        loggedIn ? (
          <>
            {props.children}
            <Pendo />
          </>
        ) : (
          <RedirectSet
            to={location.search ? `${Routes.login()}${location.search}` : Routes.login()}
          />
        )
      }
    />
  )
}

function Pendo() {
  const premiumLicenses = usePremiumLicenses()
  const teacher = useTeacher()
  const students = useStudents()
  const schools = useSchools()
  const districts = useDistricts()
  return (
    <WithResourcesInternal
      resources={{teacher, premiumLicenses, students, schools, districts}}
      render={({teacher, premiumLicenses, students, schools, districts}) => (
        <PendoInner
          teacher={teacher}
          premiumLicenses={premiumLicenses}
          students={students}
          schools={schools}
          districts={districts}
        />
      )}
      // Used directly instead of classroom's WithResources so that nothing (not a spinner or an
      // error) renders beside the page. The page keeps working without Pendo, and handles errors in
      // any of this data that it needs itself.
      error={error => <LogPendoDataError error={error} />}
      noSpinner
    />
  )
}

function LogPendoDataError({error}: {error: Error}) {
  useEffect(() => logError(error), [error])
  return null
}
